import { calculateAngle } from '../helpers/Angles';

/**
 * MediaPipe landmark indices used for cycling posture analysis (side view).
 * Left/right variants handle cyclist facing either direction.
 */
const LM = {
  LEFT_EAR: 7, RIGHT_EAR: 8,
  LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
  LEFT_WRIST: 15, RIGHT_WRIST: 16,
  LEFT_HIP: 23, RIGHT_HIP: 24,
  LEFT_KNEE: 25, RIGHT_KNEE: 26,
  LEFT_ANKLE: 27, RIGHT_ANKLE: 28,
  LEFT_HEEL: 29, RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31, RIGHT_FOOT_INDEX: 32,
};

/**
 * Bike type definitions for different fit priorities.
 */
export const BIKE_TYPES = {
  RECREATIONAL: 'recreational',
  RACE: 'race',
};

/**
 * Cycling angle definitions with evidence-based optimal ranges for injury prevention.
 *
 * Sources:
 *   Swart & Holliday 2019 (ACSM), BikeFitAdviser, BikeDynamics,
 *   Millour et al. 2019, Wadsworth & Weinrauch 2019, Velobsessive,
 *   Carlingford Active Health, Process PT, Sport & Spinal Physio
 *
 * Note: This is the base (recreational) configuration. Use getCyclingAnglesForBikeType()
 * to get bike-type-specific ranges.
 */
export const CYCLING_ANGLES = [
  {
    id: 'knee',
    label: 'Knee',
    description: 'Flexion from full extension',
    landmarks: {
      left: [LM.LEFT_HIP, LM.LEFT_KNEE, LM.LEFT_ANKLE],
      right: [LM.RIGHT_HIP, LM.RIGHT_KNEE, LM.RIGHT_ANKLE],
    },
    invertAngle: true, // display as 180 - rawAngle (flexion from extension)
    evaluateAt: 'min', // evaluate at BDC = minimum flexion value during pedal cycle
    optimalMin: 33,
    optimalMax: 45,
    warningMin: 25,
    warningMax: 55,
    riskLow: 'Saddle may be too high — risk of hamstring strain, Achilles overload, and hip rocking',
    riskHigh: 'Saddle may be too low — risk of anterior knee pain and patellar compression',
    fix: 'Adjust saddle height',
    unit: '°',
  },
  {
    id: 'hip',
    label: 'Hip',
    description: 'Shoulder-hip-knee angle',
    landmarks: {
      left: [LM.LEFT_SHOULDER, LM.LEFT_HIP, LM.LEFT_KNEE],
      right: [LM.RIGHT_SHOULDER, LM.RIGHT_HIP, LM.RIGHT_KNEE],
    },
    invertAngle: false,
    evaluateAt: 'min', // most closed position at TDC — risk of impingement
    optimalMin: 55,
    optimalMax: 95,
    warningMin: 40,
    warningMax: 110,
    riskLow: 'Hip angle too closed — risk of hip impingement, groin pain, and power loss',
    riskHigh: 'Hip angle too open — saddle may be too high or too far back',
    fix: 'Adjust handlebar height and saddle fore-aft position',
    unit: '°',
  },
  {
    id: 'ankle',
    label: 'Ankle',
    description: 'Knee-ankle-toe angle',
    landmarks: {
      left: [LM.LEFT_KNEE, LM.LEFT_ANKLE, LM.LEFT_FOOT_INDEX],
      right: [LM.RIGHT_KNEE, LM.RIGHT_ANKLE, LM.RIGHT_FOOT_INDEX],
    },
    invertAngle: false,
    evaluateAt: 'range', // track full range of motion (ankling)
    optimalMin: 85,
    optimalMax: 115,
    warningMin: 70,
    warningMax: 135,
    maxRange: 30, // more than 30° of ankle ROM through pedal stroke = excessive ankling
    riskLow: 'Excessive dorsiflexion — check saddle height and cleat position',
    riskHigh: 'Excessive plantarflexion (toe pointing) — risk of Achilles tendonitis',
    riskRange: 'Excessive ankle movement (ankling) — risk of Achilles tendon overload',
    fix: 'Adjust saddle height and cleat position; cue "quiet ankles"',
    unit: '°',
  },
  {
    id: 'torso',
    label: 'Torso',
    description: 'Back angle from horizontal',
    landmarks: {
      left: [LM.LEFT_SHOULDER, LM.LEFT_HIP],
      right: [LM.RIGHT_SHOULDER, LM.RIGHT_HIP],
    },
    isCustomCompute: true, // 2-point angle relative to horizontal, not 3-point
    invertAngle: false,
    evaluateAt: 'mean',
    optimalMin: 35,
    optimalMax: 55,
    warningMin: 20,
    warningMax: 65,
    riskLow: 'Back too flat/aggressive — risk of neck strain and lower back pain',
    riskHigh: 'Too upright — may indicate handlebar/saddle mismatch',
    fix: 'Adjust handlebar height, stem length; improve core strength and flexibility',
    unit: '°',
  },
  {
    id: 'elbow',
    label: 'Elbow',
    description: 'Shoulder-elbow-wrist angle',
    landmarks: {
      left: [LM.LEFT_SHOULDER, LM.LEFT_ELBOW, LM.LEFT_WRIST],
      right: [LM.RIGHT_SHOULDER, LM.RIGHT_ELBOW, LM.RIGHT_WRIST],
    },
    invertAngle: false,
    evaluateAt: 'mean',
    optimalMin: 150,
    optimalMax: 170,
    warningMin: 130,
    warningMax: 180,
    riskLow: 'Elbows too bent — excess weight on hands, shoulder fatigue',
    riskHigh: 'Elbows locked straight — shock transmitted to shoulders/neck, hand numbness risk',
    fix: 'Adjust reach (stem length); cue "soft elbows"',
    unit: '°',
  },
  {
    id: 'shoulder',
    label: 'Shoulder',
    description: 'Hip-shoulder-elbow angle',
    landmarks: {
      left: [LM.LEFT_HIP, LM.LEFT_SHOULDER, LM.LEFT_ELBOW],
      right: [LM.RIGHT_HIP, LM.RIGHT_SHOULDER, LM.RIGHT_ELBOW],
    },
    invertAngle: false,
    evaluateAt: 'mean',
    optimalMin: 75,
    optimalMax: 100,
    warningMin: 60,
    warningMax: 120,
    riskLow: 'Arms too close to body — cramped position, poor control',
    riskHigh: 'Reaching too far forward — risk of neck/upper back strain',
    fix: 'Adjust stem length and angle',
    unit: '°',
  },
];

/**
 * Compute all cycling angles from a set of landmarks for the given side.
 * Returns an object keyed by angle id with the computed value.
 */
export function computeCyclingAngles(landmarks, side) {
  const angles = {};

  for (const angleDef of CYCLING_ANGLES) {
    const lm = angleDef.landmarks[side];
    if (!lm) continue;

    let value;

    if (angleDef.isCustomCompute) {
      // Torso: 2-point angle relative to horizontal
      const shoulder = landmarks[lm[0]];
      const hip = landmarks[lm[1]];
      if (!shoulder || !hip) continue;

      const dx = Math.abs(shoulder.x - hip.x);
      const dy = Math.abs(shoulder.y - hip.y);
      value = Math.atan2(dy, dx) * (180 / Math.PI);
    } else {
      const pA = landmarks[lm[0]];
      const pB = landmarks[lm[1]];
      const pC = landmarks[lm[2]];
      if (!pA || !pB || !pC) continue;

      value = calculateAngle(pA, pB, pC);
      if (angleDef.invertAngle) {
        value = 180 - value;
      }
    }

    angles[angleDef.id] = value;
  }

  return angles;
}

/**
 * Determine the status (optimal / warning / risk) for a given angle value.
 */
export function getAngleStatus(angleDef, value) {
  if (value >= angleDef.optimalMin && value <= angleDef.optimalMax) {
    return { status: 'optimal', color: '#22c55e', message: 'Good' };
  }
  if (value >= angleDef.warningMin && value <= angleDef.warningMax) {
    const message = value < angleDef.optimalMin ? angleDef.riskLow : angleDef.riskHigh;
    return { status: 'warning', color: '#eab308', message };
  }
  const message = value < angleDef.warningMin ? angleDef.riskLow : angleDef.riskHigh;
  return { status: 'risk', color: '#ef4444', message };
}

/**
 * Determine which side is closer to the camera using z-depth.
 */
export function getCloserSide(landmarks) {
  const leftIndices = [LM.LEFT_SHOULDER, LM.LEFT_HIP, LM.LEFT_KNEE, LM.LEFT_ANKLE];
  const rightIndices = [LM.RIGHT_SHOULDER, LM.RIGHT_HIP, LM.RIGHT_KNEE, LM.RIGHT_ANKLE];

  let leftZ = 0, rightZ = 0;
  for (const i of leftIndices) leftZ += (landmarks[i]?.z || 0);
  for (const i of rightIndices) rightZ += (landmarks[i]?.z || 0);

  return (leftZ / leftIndices.length) < (rightZ / rightIndices.length) ? 'left' : 'right';
}

/**
 * Get the landmark pixel positions for drawing an angle overlay.
 * Returns the three (or two for torso) landmark positions scaled to canvas dimensions.
 */
export function getAngleLandmarkPositions(angleDef, landmarks, side, width, height) {
  const lm = angleDef.landmarks[side];
  if (!lm) return null;

  return lm.map(idx => {
    const p = landmarks[idx];
    if (!p) return null;
    return { x: p.x * width, y: p.y * height };
  });
}

/**
 * Get cycling angle definitions adjusted for specific bike type.
 * @param {string} bikeType - 'recreational' or 'race'
 * @returns {Array} - Angle definitions with bike-type-specific ranges
 */
export function getCyclingAnglesForBikeType(bikeType = BIKE_TYPES.RECREATIONAL) {
  if (bikeType === BIKE_TYPES.RACE) {
    // Race bike: tighter ranges for aggressive/aero position
    return CYCLING_ANGLES.map(angle => {
      const raceAngle = { ...angle };
      
      switch (angle.id) {
        case 'knee':
          // Race: 33-40° (tighter for efficiency)
          raceAngle.optimalMin = 33;
          raceAngle.optimalMax = 40;
          raceAngle.warningMin = 25;
          raceAngle.warningMax = 48;
          raceAngle.riskLow = 'Saddle too high for race position — risk of hamstring strain and power loss in sprints';
          raceAngle.riskHigh = 'Saddle too low for race position — risk of anterior knee pain and reduced efficiency';
          break;
          
        case 'hip':
          // Race: 55-80° (more closed for aero)
          raceAngle.optimalMin = 55;
          raceAngle.optimalMax = 80;
          raceAngle.warningMin = 45;
          raceAngle.warningMax = 95;
          raceAngle.riskLow = 'Hip angle too closed for race position — risk of hip impingement in aero position';
          raceAngle.riskHigh = 'Hip angle too open — compromises aerodynamics and power transfer';
          break;
          
        case 'torso':
          // Race: 25-45° (more aggressive back angle)
          raceAngle.optimalMin = 25;
          raceAngle.optimalMax = 45;
          raceAngle.warningMin = 15;
          raceAngle.warningMax = 55;
          raceAngle.riskLow = 'Torso too flat for sustained racing — extreme aero position requires excellent core strength';
          raceAngle.riskHigh = 'Torso too upright for race bike — compromises aerodynamics';
          break;
          
        case 'elbow':
          // Race: 150-165° (slightly more bent for aero bars/drops)
          raceAngle.optimalMin = 150;
          raceAngle.optimalMax = 165;
          raceAngle.warningMin = 135;
          raceAngle.warningMax = 175;
          break;
          
        case 'ankle':
        case 'shoulder':
        default:
          // Keep same ranges for ankle and shoulder
          break;
      }
      
      return raceAngle;
    });
  }
  
  // Recreational: return original ranges
  return CYCLING_ANGLES;
}

/**
 * Get bike type display name.
 * @param {string} bikeType - Bike type constant
 * @returns {string} - Display name
 */
export function getBikeTypeDisplayName(bikeType) {
  switch (bikeType) {
    case BIKE_TYPES.RACE:
      return 'Race/Performance';
    case BIKE_TYPES.RECREATIONAL:
      return 'Recreational/Endurance';
    default:
      return 'Unknown';
  }
}
